import i18next from 'i18next';
import dayjs from 'dayjs';
import {DATE_FORMAT, LANGUAGES_ISO_3, type PrivacyPolicySettings} from '@chekin/core';

function dataURIToBlob(dataURI?: string | null) {
  if (!dataURI) {
    return;
  }

  const splitDataURI = dataURI.split(',');
  const byteString =
    splitDataURI[0].indexOf('base64') >= 0
      ? atob(splitDataURI[1])
      : decodeURI(splitDataURI[1]);
  const mimeString = splitDataURI[0].split(':')[1].split(';')[0];

  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);

  return new Blob([ia], {type: mimeString});
}

function getSignaturePayLoadValue(
  signature: string | null,
): string | Blob | undefined | null {
  if (signature) {
    return dataURIToBlob(signature);
  }
  if (!signature && typeof signature === 'string') {
    return '';
  }

  return;
}

const getPayloadDate = (date: Date | null) => {
  return date ? dayjs(new Date(date)).format(DATE_FORMAT) : undefined;
};

const buildSelectOption = (value?: string) => {
  if (value) {
    return {label: '', value: value};
  }
};

function clearValue<T, D = T>(value: T, transformedValue?: D) {
  if (!value) {
    return;
  }

  return transformedValue || value;
}

function getCurrentLocale() {
  try {
    return i18next.language.slice(0, 2);
  } catch {
    return 'en';
  }
}

function resetTimeInDate(date?: string | Date) {
  if (!date) {
    return;
  }

  if (date && typeof (date === 'string' || date instanceof Date)) {
    const updatedDate = date instanceof Date ? date : new Date(date);
    updatedDate.setHours(0, 0, 0, 0);
    return updatedDate;
  }
}

export {
  isPdfUrl,
  dataURIToBlob,
  getSignaturePayLoadValue,
  getPayloadDate,
  buildSelectOption,
  clearValue,
  getCurrentLocale,
  resetTimeInDate,
};

function isPdfUrl(url?: string) {
  if (!url) return false;

  try {
    return new URL(url, window.location.href).pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return false;
  }
}

function downloadFromLink(link?: string, blank: '_blank' | '_no-blank' = '_blank') {
  if (!link) return;
  if (blank === '_no-blank') {
    window.location.href = link;
    return;
  }

  const isSwitched = window.open(link, blank);

  if (!isSwitched) {
    const anchorLink = document.createElement('a');
    anchorLink.href = link;
    anchorLink.click();
    anchorLink.remove();
  }
}

const getDocumentDownloadByLanguage = (data: PrivacyPolicySettings, language: string) => {
  const defaultLanguage = data.default_language;
  const documents = data.documents;

  const documentInCurrentLanguage = documents.find(document => {
    return document.language === LANGUAGES_ISO_3[language] || false;
  });

  if (documentInCurrentLanguage) {
    return documentInCurrentLanguage.document_download;
  }

  const documentInDefaultLanguage = documents.find(
    document => document.language === defaultLanguage,
  );

  if (documentInDefaultLanguage) {
    return documentInDefaultLanguage.document_download;
  }

  return '';
};

export {downloadFromLink, getDocumentDownloadByLanguage};

const DISPLAY_LOCALE_FALLBACK = 'en-GB';
const LONG_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

function toDisplayLocale(language?: string) {
  if (!language) return DISPLAY_LOCALE_FALLBACK;
  return language.startsWith('en') ? DISPLAY_LOCALE_FALLBACK : language;
}

/** Locale-aware date for display; UTC so calendar dates never shift. */
function formatDisplayDate(
  value?: string | Date | null,
  language?: string,
  options: Intl.DateTimeFormatOptions = LONG_DATE_OPTIONS,
) {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  try {
    return new Intl.DateTimeFormat(toDisplayLocale(language), {
      timeZone: 'UTC',
      ...options,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(DISPLAY_LOCALE_FALLBACK, {
      timeZone: 'UTC',
      ...options,
    }).format(date);
  }
}

function getInitials(source: string) {
  return source
    .split(/[\s@.]+/)
    .map(part => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** `X1234567L` → `•••567L` */
function maskDocument(value: string) {
  if (value.length <= 4) return value;

  return `${'•'.repeat(3)}${value.slice(-4)}`;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function countNights(checkIn?: string, checkOut?: string) {
  if (!checkIn || !checkOut) return null;

  const from = new Date(checkIn).getTime();
  const to = new Date(checkOut).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return null;

  const nights = Math.round((to - from) / MS_PER_DAY);
  return nights > 0 ? nights : null;
}

export {formatDisplayDate, toDisplayLocale, getInitials, maskDocument, countNights};
